import { IAssetLock } from "@entities/igloo/types"

import { closureType } from "../utils/Constant"

import { AffiliateType } from "./AffiliateTypes"
import {
  CheckInTypes,
  GetCartResponseType,
  LeviesType,
  ParticipantsUnitType,
  PaymentTypes,
  PerBookingFieldTypeInBookingFields,
  TipSourceType,
  TipType,
} from "./CartTypes"
import { GiftCardValidityResponseType } from "./GiftCardType"
import { BookingsOfASlotType } from "./ManifestTypes"
import { WaiverCountTypes } from "./ManifestTypes"
import { IReminder } from "./ProductExtraTypes"
import { ProductPricingFieldType } from "./ProductPricingTypes"
import { Filetype, ProductAddonType } from "./ProductTypes"
import { IDomain, MetaDataType, PaginationLinkType } from "./UserTypes"
import { NoteTypesEnum } from "./ViewOrderTypes"

export enum ProductCategoryTypes {
  GIFT_CARD = "gift_card",
  // RENTAL = 'rental',
  MERCHANDIZE = "merchandise",
  BOOKABLE = "bookable",
  ADD_ONS = "add_ons",
  GIFT_CARDS = "gift_card",
  EXPENSE = "expense",
  MEMBERSHIP = "memberships",
}

export enum OrderStatusTypes {
  // NEW = 'new',
  // INVENTORY_CONTROL = 'inventory_control',
  // PAYMENT_PENDING = 'payment_pending',
  // DEPOSIT = 'deposit',
  // CONFIRMED = 'confirmed',
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  CONFIRMED = "confirmed",
  PENDING = "pending",
  PAYMENT_PENDING = "payment_pending",
  PENDING_CONFIRM = "pending_confirm",
}

export const OrderStatuses = [OrderStatusTypes.CONFIRMED, OrderStatusTypes.PENDING_CONFIRM]

export enum OrderChannelTypes {
  OFFLINE = "offline",
  ONLINE = "online",
}

export enum OrderPlatformTypes {
  DESKTOP_WEBSITE = "desktop-website",
  MOBILE_WEBSITE = "mobile-website",
  MOBILE_IOS = "mobile-ios",
  MOBILE_ANDROID = "mobile-Android",
}

export enum TransactionStatusTypes {
  SUCCESSFUL = "successful",
  FAILED = "failed",
  PENDING = "pending",
  REFUNDED = "refunded",
}

export enum TransactionRefundType {
  CAPTURE = "capture",
  REFUND = "refund",
  AUTHORIZE = "authorize",
  CANCEL_AUTHORIZATION = "cancel_authorization",
}

export enum PaymentHoldType {
  HOLD = "HOLD",
  RELEASED = "RELEASED",
  CAPTURED = "CAPTURED",
  CANCELLED = "CANCELLED",
}

export interface IntegrationType {
  id: string
  name: string
}

export interface OrderItem {
  id: string
  product: Product
  quantity: number | null
  sub_total: number
  formatted_sub_total: string
  total: number
  formatted_total: string
  offer_total: number
  formatted_offer_total: string
  levies_total: number
  formatted_levies_total: string
  currency: string
  created_at: string
}

export interface Product {
  id: string
  created_at: string
  updated_at: string
  deleted_at: string | null
  name: string
  code: string
  product_type_id: string
  description: string
  visibility: string
  terms_and_conditions: string | null
  cancellation_policy: string | null
  status: string
  tenant_id: string
  created_by: string
  domain_id: string
}

export interface ProductMembershipType {
  price: {
    price: number
    display_price: string
    price_formatted: string
  }
  validity: {
    end_at: string // YYYY-MM-DD
    start_at: string
    expiry_type: string
    expiry_value: number
  }
  limitation: {
    duration_type: string
    duration_value: number
    max_no_of_booking: number
    has_booking_limitation: boolean
    can_combine_with_offers: boolean
  }
  product_type: string
}

export interface ProductMembershipDiscountType {
  id: string
  title: string
  product_type: string
  amount_type: string
  amount: number
  priority: number
  start_at: string
  end_at: string
  applicable_days: number[]
  status: string
  products: {
    id: string
    product: {
      id: string
      name: string
    }
    membership_product: {
      id: string
      name: string
    }
  }[]
}

export interface ProductType {
  id: string
  name: string
  code: string
  description: string
  terms_and_conditions: string | null
  cancellation_policy: string | null
  widget_description: string | null
  has_required_info: boolean
  visibility: string
  is_rental: boolean
  is_dynamic_rental: boolean
  is_multi_day_rental: boolean
  is_flexible_timings_rental: boolean
  status: string
  order: number
  mediaFiles?: Filetype[] | null
  deposit: {
    amount: string
    type: string
  } | null
  advertise_price?: string
  reminders: IReminder[]
  prices: ProductPricingFieldType[]
  type: {
    id: string
    name: string
    is_rental?: boolean
    description: string
    internal_type: ProductCategoryTypes
  }
  categories: [
    {
      id: string
      name: string
      type: string
      description: string | null
      created_at: string
      visibility: string
      updated_at: string
      cover_image: string | null
    },
  ]
  availability: ProductAvailabilityType
  merchandise?: ProductAddonType["merchandise"]
  gift_card: GiftCardValidityResponseType["data"] | null
  membership?: ProductMembershipType
  minimum_giftcard_value: number | null
  minimum_participants: number
  minimum_participants_booking_display_message: string
  has_addon: number
  auto_confirm_booking: boolean
  block_resources_on_pending: boolean
  first_available_date?: string
  unit_types_labels: {
    multi_day: string
    single_day_flexi: string
    single_day_fixed: string
    duration_type: string
  }
  created_at: string
  last_available_end_date: string
  enable_overusing_assets: boolean
  customer_booking_setting?: {
    rebook?: {
      enable: boolean
      duration_type?: string
      duration_value?: number
    }
    cancellation?: {
      enable: boolean
      duration_type?: string
      duration_value?: number
    }
  }
  is_unit_assets_linked: boolean
  membershipDiscounts?: ProductMembershipDiscountType[]
  membershipProductLimitationCount?: number
  membershipProducts?: ProductMembershipDiscountType["products"]
  applicableMembershipDiscounts: boolean
  widget_quantity_selector_label?: string | null
  reopenable_for?: closureType | null
}

export interface ProductCategoryType {
  id: string
  name: string
  type: string
  description: string | null
  created_at: string
  visibility: string
  updated_at: string
  assets: []
  products: ProductType[]
  activeProducts: ProductType[]
  cover_image: string | null
}
export interface GetCategoriesResponseType {
  data: ProductCategoryType[]
}

export interface ProductAvailabilityType {
  product_id: string
  tenant_id: string
  domain_id: string
  created_by: string
  updated_at: string
  deleted_at: string | null
  id: string
  created_at: string
  name: string
  duration: number
  duration_type: string
  increment: number
  increment_type: string
  start_date: string
  end_date: string
  start_time: string
  end_time: string
  all_day: number
  available_days: number[]
  assets: string[]
  maintenance: string | null
  maintenance_type: string
  cut_off: string | null
  cut_off_type: string
  crew_count: number
  interval_between_slots: number | null
  interval_between_slots_type: string
}
export interface GetAvailabilityResponseType {
  data: ProductAvailabilityType[]
}

export interface GetAvailabilityForMonthResponseType {
  data: Record<
    string,
    {
      crew: {
        attachable_type: string
        attachable_id: string
        count: number
      }
      has_call_to_book: boolean
      slot_local_date: string // YYYY-MM-DD
      available: number
      booked: number
      bookings_count: number
      closed: number
      hold: number
      is_reopenable: boolean
      slot_date: string
      slots: []
      un_reopenable_reason: string | null
    }
  >
}

export interface GetAvailabilityForDateResponseType {
  [key: string]: {
    availability_id: string
    start_date: string // YYYY-MM-DD
    end_date: string // YYYY-MM-DD
    reopenable_for: closureType | null
    reopenable_reason: string | null
    is_slot_unavailable_due_to_past_time: boolean
    bifurcation: Record<
      string,
      {
        total_capacity: number
        capacity_per_quantity: number
        available: number
        booked: number
        hold: number
        closed: number
      }
    >
    crew: {
      attachable_type: string
      attachable_id: string
      count: number
    }
    slot: string
    available: number
    booked: number
    bookings_count: number
    closed: number
    end_time: string
    hold: number
    is_reopenable: boolean
    local_end_time: string
    local_start_time: string
    slot_id: string
    slot_uuid: string
    start_time: string
    timezone: string
    un_reopenable_reason: string | null
    utc_end_time: string
    utc_start_time: string
  }
}

export interface CustomerTypeOrder {
  id: string
  creator_system_meta: string | null
  creator_system: string
  tenant_id: string
  domain_id: string
  created_by: string | null
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
  address1: string | null
  address2: string | null
  city: string | null
  state: string | null
  country: string | null
  zip_code: string
  created_at: string
  updated_at: string
  status: string
  deleted_at: string | null
  receive_marketing_emails: boolean
  receive_sms_updates: boolean
  full_name: string
  is_member: number
  phone_verified_at: string | null
  email_verified_at: string
  member_code: string
  password: string
  google_id: string
  language_iso2: string
  profile_image: string | null
  remember_token: string
}

export interface GetCustomersResponseType {
  data: CustomerTypeOrder[]
  links: PaginationLinkType
  meta: MetaDataType
}

export interface AddCustomerRequestType {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
  status: string
}

export interface AddCustomerResponseType {
  message: string
  customer: CustomerTypeOrder
}

export interface ProductFieldRulesType {
  id: string
  attachable_id: string
  display_type: string
  is_hidden: boolean
  attachable_type: string
  rules: {
    rules: {
      type: string
      value: string
      operator: string
      product_field_id: string | null
      condition?: string
      rules: ProductFieldRulesType["rules"][0]["rules"]
    }[]
    outcome?: string
    condition: string
  }[]
}
export interface ProductFieldType {
  id: string
  created_at: string
  updated_at: string
  tenant_id: string
  domain_id: string
  product_id: string
  field_id: string
  dont_show_during_order: boolean
  allowed_internal_booking: boolean
  required_for_internal_booking: number
  required_for_online_booking: number
  required_for_affiliate_booking: number
  allowed_online_booking: boolean
  allowed_affiliate_booking: boolean
  required: boolean
  order: number
  label: string
  value: string | null
  options?: {
    name: string
    label: string
  }[]
  placeholder: string
  validation_rules: string | null
  validation_message: string | null
  ask: string[]
  field: {
    id: string
    created_at: string
    updated_at: string
    tenant_id: string
    domain_id: string
    name: string
    type: string
  }
  product_field_id?: string
  rules: ProductFieldRulesType[]
}

export interface GetProductFieldsResponseType {
  data: ProductFieldType[]
}

export enum PlatformType {
  PLATFORM_WEB = "web",
  PLATFORM_API = "api",
  PLATFORM_AFFILIATE = "affiliate",
}
export interface CreateOrderRequestType {
  cart_id: string | undefined
  payment_token?: string
  payment_method?: string | undefined | null
  payment_type?: PaymentTypes
  order_id?: string
  payment_intent_id?: string
  amount?: number
  reason?: string
  save_card_on_file?: boolean
  from_saved_card?: boolean
  gift_card_code?: string
  name_on_card?: string
  other_payment_type?: string
  other_reference_no?: string
}
export interface OrderCustomerType extends CustomerTypeOrder {
  tenant_id: string
  domain_id: string
  created_by: string
  deleted_at: string | null
}
export interface OrderMetaItemQuantityUnitType extends ParticipantsUnitType {
  deleted_at: string | null
  is_default: number
  tenant_id: string
  created_by: string
  domain_id: string
}
export interface OrderMetaItemQuantityType {
  id: string
  created_at: string
  updated_at: string
  cart_item_id: string
  unit_id: string
  quantity: number
  unit: OrderMetaItemQuantityUnitType
  cart_item: {
    id: string
    created_at: string
    updated_at: string
    cart_id: string
    product_id: string
    slot_id: string
    slot_reserve_expires_at: string
  }
  price: {
    id: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    unit_id: string
    product_id: string
    tenant_id: string
    domain_id: string
    pricing_structure_type: string
    advertise_price: number
    max_quantity: number
    price: number
    price_type: string
  }
}
export interface OrderMetaItemLeviesType {
  id: string
  name: string
  type: number
  value: string
  status: string
  category: number
  tenant_id: string
  domain_id: string
  created_at: string
  created_by: string
  deleted_at: string | null
  updated_at: string
  is_compounded: boolean
  is_price_inclusive: boolean
  laravel_through_key: string
  type_name: string
}
export interface OrderMetaItemProductType {
  id: string
  code: string
  meta: string | null
  name: string
  type: {
    id: string
    name: string
    created_at: string
    deleted_at: string | null
    updated_at: string
    internal_type: ProductCategoryTypes
    description: string
  }
  status: string
  domain_id: string
  tenant_id: string
  created_at: string
  created_by: string
  deleted_at: string | null
  updated_at: string
  visibility: string
  description: string
  product_type_id: string
  cancellation_policy: string
  terms_and_conditions: string
  prices: {
    id: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    unit_id: string
    product_id: string
    tenant_id: string
    domain_id: string
    pricing_structure_type: string
    advertise_price: number
    max_quantity: number
    price: number
    price_type: string
  }
}
export interface OrderMetaItemType {
  id: string
  cart: {
    id: string
    status: string
    currency: string
    order_id: string | null
    domain_id: string
    tenant_id: string
    created_at: string
    deleted_at: string | null
    offer_code: string | null
    updated_at: string
    customer_id: string
    affiliate_id: string | null
  }
  levies: OrderMetaItemLeviesType[]
  cart_id: string
  product: OrderMetaItemProductType
  slot_id: string
  created_at: string
  product_id: string
  quantities: OrderMetaItemQuantityType[]
  updated_at: string
  slot_reserve_expires_at: string
}
export interface OrderMetaType {
  id: string
  items: OrderMetaItemType[]
  status: string
  currency: string
  order_id: string
  domain_id: string
  tenant_id: string
  created_at: string
  deleted_at: string | null
  offer_code: string | null
  updated_at: string
  customer_id: string
  affiliate_id: string
  display_total: string
  total_in_cents: number
  formatted_total: string
  display_subtotal: string
  discount_in_cents: number
  subtotal_in_cents: number
  formatted_subtotal: string
  display_total_levies: string
  total_levies_in_cents: number
  formatted_total_levies: string
}
export interface CreateOrderResponseType {
  code?: string
  payload?: {
    client_secret?: string
    order_id: string
  }
  message: string
  order: GetOrderDetailsResponseType
}

export interface GetAllOrdersDataType {
  cancellation_reason: string | null
  channel: string
  created_at: string
  created_by: string | null
  creator_type: string
  currency: string
  customer: OrderCustomerType
  customerMembership?: CustomerMembershipResponseType["customerMembership"]
  id: string
  items: OrderItemType[]
  order_number: string
  status: string
  total_due: number
  total_due_formatted: string
  total_formatted: string
  total_paid_formatted: string
}

export interface GetAllOrdersResponseType {
  data: GetAllOrdersDataType[]
  links: PaginationLinkType
  meta: MetaDataType
}
export interface GetRefundMethodsType {
  label: string
  value: string
}
export interface GetAllProcutAvailibleResponseType {
  availability: GetAvailabilityForDateResponseType[]
  category: ProductCategoryType
  description: string
  product_id: string
  product_name: string
}

export interface TransactionType {
  id: string
  created_at: string
  integration: {
    id: string
    name: string
  }
  order_id: string
  order_item_id: string | null
  currency: string
  amount: number
  method: string | null
  refunded_amount: number
  formatted_refunded_amount: string
  formatted_amount: string
  amount_available_for_refund?: number
  formatted_amount_available_for_refund?: string
  status: TransactionStatusTypes
  type: string // authorize, cancel_authorization, capture, refund
  refund_reason?: string
  card_brand: string
  card_last4: string
  card_exp_month: string
  card_exp_year: string
  name_on_card: string | null
  in_person_refund?: boolean
  other_ref?: {
    payment_type: string
    reference_no: string
  }
  gift_card?:
    | {
        id: string
        code: string
        purchased_amount: {
          display_purchased_amount: string
          purchased_amount: number
          purchased_amount_formatted: string
          purchased_amount_in_units: string
        }
        remaining_balance: {
          display_remaining_amount: string
          remaining_amount: number
          remaining_amount_formatted: string
          remaining_amount_in_units: number
        }
      }[]
    | null
}

export interface GetTransactionsResponseType {
  data: TransactionType[]
  links: PaginationLinkType
  meta: MetaDataType
}

export interface OrderItemProductType extends ProductType {
  updated_at: string
  deleted_at: string | null
  product_type_id: string
  meta: {
    merchandise?: ProductAddonType["merchandise"]
    gift_card: GiftCardValidityResponseType["data"] | null
    membership?: ProductMembershipType | null
  } | null
  tenant_id: string
  auto_confirm_booking: boolean
  created_by: string
  domain_id: string
  has_add_on: number
  availabilities: {
    id: string
    product_id: string
    tenant_id: string
    domain_id: string
    created_by: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    name: string
    duration: number
    duration_type: string
    increment: number
    increment_type: string
    start_date: string
    end_date: string
    start_time: string
    end_time: string
    all_day: number
    available_days: number[]
    assets: string[]
  }[]
  quantities: {
    name: string
    quantity: number
    formatted_price: string
  }
  customer_booking_setting?: {
    rebook?: {
      enable: boolean
      duration_type?: string
      duration_value?: number
    }
    cancellation?: {
      enable: boolean
      duration_type?: string
      duration_value?: number
    }
  }
}
export interface OrderItemType {
  id: string
  booking: {
    id: string
    order_item_id: string
    booking_number: string
    slot_id: string
    slot_uuid: string
    domain_id: string
    status: OrderStatusTypes
    igloo_lock: IAssetLock[]
    product_availability_slot: {
      id: string
      created_at: string
      updated_at: string
      product_id: string
      tenant_id: string
      domain_id: string
      assets: null
      product_availability_id: string
      start_at: string
      end_at: string
      cancelled_at: string | null
      available: number
      booked: number
      hold: number
      is_real_time_created: number
      deleted_at: string | null
      crew_count: number
      local_start_time: string
      local_end_time: string
      availability: ProductAvailabilityType
      product: {
        id: string
        name: string
        product_type_id: string
        has_add_on: number
        type: {
          id: string
          name: string
          internal_type: ProductCategoryTypes
        }
        domain: IDomain
      }
      bookings: {
        id: string
        domain_id: string
        tenant_id: string
        order_item_id: string
        product_id: string
        checked_in: number
        cancelled_at: string | null
        cancellation_reason: string | null
        check_in_date: string | null
        check_out_date: string | null
        slot_id: string
        status: OrderStatusTypes
        created_at: string
        updated_at: string
        cart_item_id: string
        booking_number: string
        slot_uuid: string
        crew_count: number
        deleted_at: string | null
      }[]
      product_availability_slot_with_trashed: OrderItemType["booking"]["product_availability_slot"]
    }
    domain: IDomain
    product_availability_slot_with_trashed?: OrderItemType["booking"]["product_availability_slot"]
    crew_count: number
    cancellation_reason?: string | null
  }
  product: OrderItemProductType
  quantity: number | null
  slot_id: string
  sub_total: number
  formatted_sub_total: string
  total: number
  formatted_total: string
  offer_total: number
  formatted_offer_total: string
  membership_total: number
  formatted_membership_total: string
  levies_total: number
  formatted_levies_total: string
  levies: LeviesType[]
  currency: string
  meta: string | null
  created_at: string
  giftcard?: {
    code: string
    id: string
  }
  quantities: {
    id: string
    created_at: string
    updated_at: string
    unit: ParticipantsUnitType
    price_in_cents: number
    display_price: string
    formatted_price: string
    display_total: string
    formatted_total: string
    quantity: number
    participants: {
      fields: Record<string, string | null>
      id: string
      created_at: string
      updated_at: string
      booking_id: string
      order_item_id: string
      product_id: string
      domain_id: string
      tenant_id: string
      cart_participant_id: string
      checked_in: CheckInTypes
      cancelled_at: string | null
      cancellation_reason: string | null
      check_in_date: string | null
      check_out_date: string | null
      product_availability_slot_id: string
      status: string
      unit_id: string
    }[]
  }[]
  notes: GetOrderDetailsResponseType["notes"]
  agreements: []
  parent_id?: string | null
  booking_participant_id: string | null
  description: string | null
  discounted_subtotal: number
  formatted_discounted_subtotal: string
  membership_discount_json?: {
    id: string
    title: string
    amount: number
    amount_type: string // percentage
  }
  membership_discount_with_add_ons?: {
    membership_discount_with_add_ons: number
    membership_discount_with_add_ons_in_units: number
    display_membership_discount_with_add_ons: string
    membership_discount_with_add_ons_formatted: string
  }
  cancellation_reason?: string | null
  waiver_count?: WaiverCountTypes
  can_rebook?: boolean
  can_cancel?: boolean
}
export interface GetOrderDetailsResponseType {
  id: string
  created_at: string
  updated_at: string
  order_number: string
  status: OrderStatusTypes
  payment_status: string
  items: OrderItemType[]
  currency: string
  customer: OrderCustomerType
  invoice: {
    id: string
    order_num: string
    order_id: string
    invoice_name: string
    invoice_date: string
    invoice_description?: string
    operator: {
      id: string
      email: string
      partner_name: string | null
      stripe_id: string
      pm_type: string | null
      pm_last_four: string | null
      trial_ends_at: string
      status: string
      created_at: string
      updated_at: string
      application_fee_inclusive: boolean
      data: {
        country: string
        domain_id: string
        tenant_id: string
        created_by: string
        currency_iso3: string
        timezone_iso3: string
        business_hours: { day: string; open_time: string; close_time: string }[]
        week_starts_on: string
      }
      real_operator: number
      type: string
      ready: boolean
      domain: string
      password: string
      last_name: string
      first_name: string
      tenant_id: string
      is_primary: boolean
      is_fallback: boolean
      location_name: string
      company: string
      website: string | null
      primary_industry: string
      phone: string
      dial_code: number
      company_description: string
      address_1: string
      address_2: string | null
      city: string
      state: string
      zip: string
      country: string
      timezone_iso3: string
      currency_iso3: string
      language_iso2: string
      order_number_format: string
      order_number_prefix: string
      date_format: string
      time_format: string
      internal_order_confirm_on_create: boolean
      auto_confirm_booking: boolean
      payment_driver: string | null
      booking_number_prefix: string | null
      affiliate_invoice_prefix: string
      tip_status: string
      tip_type: string
      tip_options: { amount: number }[]
      is_custom_tip_allowed: boolean
      logo: string
      hold_slots_during_cart_building: boolean
      show_taxes_fees_breakdown: boolean
      cancellation_reasons: string[]
      quantity_indicator: string
      show_levy_breakdown_in_widget: boolean
      enable_per_participant_waiver: number
      enable_per_participant_documents: number
      enable_per_booking_waiver: number
      enable_per_booking_documents: number
      cancel_past_bookings_on_order_cancellation: number
      email_tenant_on_booking: boolean
      enable_dropdown_in_order_placement: boolean
      enable_staff_assignments: boolean
      enable_tip_staff_per_staff: boolean
      allow_past_bookings: boolean
      giftcard_number_prefix: string | null
      enable_dynamic_pricing: boolean
      ga_tracking: string | null
      enable_deposit_option: boolean
      enable_expense_discount: boolean
      member_code_prefix: string | null
      tip_status_in_widget: string
      tip_number: string
      tip_notes: string
      enable_membership: boolean
      enable_customer_login: boolean
    }
    order: {
      total_discount: number
      total_discount_formatted: string
      offer_code: string | null
      subtotal: number
      subtotal_formatted: string
      total_amount: number
      total_formatted: string
      total_refund: number
      total_refund_formatted: string
      total_paid: number
      total_paid_formatted: string
      total_due: number
      total_due_formatted: string
      balance: number
      balance_formatted: string
      total_levies_formatted: string
      total_levies: number
      total_tips_formatted: string
      membership_discount: number
      total_membership_formatted: string
      levies: {
        id: string
        levy_id: string
        name: string
        is_compounded: boolean
        is_price_inclusive: boolean
        amount: number
        formatted_amount: string
        currency: string
        category_name: string
        type_name: string
        value: string
      }[]
      currency: string
      customerMembership: any | null
    }
    customer: {
      id: string
      creator_system_meta: any | null
      creator_system: string
      tenant_id: string
      domain_id: string
      created_by: string
      first_name: string
      last_name: string
      email: string
      stripe_customer_id: string
      phone: string
      phone_code: string
      address1: string | null
      address2: string | null
      city: string | null
      state: string | null
      country: string | null
      zip_code: string | null
      status: string
      created_at: string
      updated_at: string
      deleted_at: string | null
      receive_marketing_emails: boolean
      receive_sms_updates: boolean
      full_name: string
      is_member: number
      phone_verified_at: string | null
      email_verified_at: string | null
      member_code: string | null
      google_id: string | null
      language_iso2: string
      profile_image: string | null
    }
    items: {
      product: {
        category: string
        time: string
        name: string
        description: string
        rate: string
        quantities: {
          name: string
          quantity: number
          price: number
          display_price: number
          formatted_price: string
          display_total: number
          formatted_total: string
          is_multi_day: boolean
          is_rental: boolean
        }[]
        membership_total: number
        formatted_membership_total: string
        local_start_at: string
        local_end_at: string
        membership: {
          validity: {
            expiry_type: string
            expiry_value: number
            start_at: string
            end_at: string
          }
        }
      }
    }[]
    transactions: {
      id: string
      created_at: string
      integration: {
        id: string
        name: string
      }
      order_id: string
      order_item_id: null
      currency: string
      amount: number
      refunded_amount: number
      formatted_refunded_amount: string
      formatted_amount: string
      amount_available_for_refund: number
      formatted_amount_available_for_refund: string
      status: string
      type: string
      refund_reason: null
      card_brand: string
      card_last4: string
      card_exp_month: string
      card_exp_year: string
      name_on_card: string
    }[]
    notes: string | null
    // TODO - needs more filling out - need an invoiceID that has these fields filled in API call
    receipts: {
      name: string
      amount_formatted: string
      transaction: { created_at: string }
    }[]
    payment_holds: any[]
  }
  total_discount_in_cents: number
  total_discount: number
  total_discount_formatted: string
  membership_discount_in_cents: number
  membership_discount: number
  total_membership_formatted: string
  offer_code: string | null
  discount_code: string | null
  total_tip_in_cents: number
  total_tip: number
  total_tip_formatted: string
  subtotal_in_cents: number
  subtotal: number
  subtotal_formatted: string
  discounted_subtotal_in_cents: number
  discounted_subtotal: number
  formatted_discounted_subtotal: string
  total_amount_in_cents: number
  total_amount: number
  total_formatted: string
  total_refund_in_cents: number
  total_refund: number
  total_refund_formatted: string
  total_paid_in_cents: number
  total_paid: number
  total_paid_formatted: string
  deposit_with_tip_in_cents: number
  deposit_with_tip: string
  formatted_deposit_with_tip: string
  total_due_in_cents: number
  total_due: number
  total_due_formatted: string
  total_levies_in_cents: number
  total_levies: number
  total_levies_formatted: string
  levies: LeviesType[]
  balance_in_cents: number
  balance: number
  balance_formatted: string
  notes: {
    created_at: string
    updated_at: string
    id: string
    content: string
    notable: {
      id: string
      type: NoteTypesEnum
    }
    created_by: {
      id: string
      name: string
    }
  }[]
  refundable_amount_in_cents: number
  refundable_amount: number
  refundable_amount_formatted: string
  created_by: string | null
  affiliate: AffiliateType
  commission: []
  meta: OrderMetaType
  cart_id: string
  tips?: {
    type: TipType
    value: string
    source: TipSourceType
    status: string
    amount_in_cents: number
    amount: number
    formatted_amount: string
    fee_in_cents: number
    fee: number
    formatted_fee: string
  }[]
  waiver?: {
    total: number
    signed: number
    unsigned: number
  }
  form: {
    total: number
    signed: number
    unsigned: number
  }
  checklist: {
    total: number
    signed: number
    unsigned: number
  }
  survey: {
    total: number
    signed: number
    unsigned: number
  }
  channel: string
  platform: string
  crew: {
    attachable_type: string
    attachable_id: string
    count: number
  }
  member_id: string
  customerMembership: {
    id: string
    start_at: string
    end_at: string | null
    product: OrderItemProductType
    code: string
    cancellation_date: string | null
    cancellation_reason: string | null
    is_active: number
    order: GetOrderDetailsResponseType
  }
  cancellation_reason?: string | null
}

export type GetOrderCheckInDetailsResponseType = {
  data: BookingsOfASlotType[]
}
export interface UpdateParticipantCheckInStatusRequestType {
  checked_in: number
}
export interface UpdateParticipantCheckInStatusResponseType {
  id: string
  checked_in: string
  check_in_date: string
  check_out_date: string | null
  status: string
  fields: Record<string, string | number | boolean | string[]>
}

export interface UpdateOrderCheckInStatusResponseType {
  id: string
  product_id: string
  checked_in: number
  cancelled_at: string | null
  cancellation_reason: string | null
  check_in_date: string
  check_out_date: string | null
  slot_id: string
  status: string
  created_at: string
  updated_at: string
}

export interface UpdateOrderStatusRequestType {
  status: OrderStatusTypes
  cancellation_reason?: string
  setting_id?: string
  body?: string
}
export interface UpdateOrderStatusResponseType {
  message: string
  order: CreateOrderResponseType
}

export enum RefundTypes {
  CASH = "CASH",
  GIFTCARD = "GIFTCARD",
  VOUCHER = "VOUCHER",
}

export interface RefundFormAttributes {
  refundType: string
  amount: number
  reason: string
  transaction: string
  refunded_amount?: number
  refund_communication_template_id?: string
}

export interface PayOrderRequestType {
  payment_method?: string
  payment_token?: string
  order_id: string
  amount?: number
  name_on_card?: string
  from_saved_card?: boolean
  tip?: {
    type: TipType
    value: string | number
    source: TipSourceType
  }
}

export type UpdateOrderItemType = {
  product_id: string
  quantities: {
    unit_id: string
    quantity: number
  }[]
  slot_id: string
  is_updated: 1 | 0 // always pass in is_updated=0, if you are adding new item to order; is_updated=1, if you are editing old item from order.
  order_item_id?: string
}
export interface UpdateOrderRequestType {
  customer_id: string
  order_id: string
  items: UpdateOrderItemType[]
}

export interface DiscardCartResponseType {
  message: string
}

export interface SaveCartToOrderRequestType {
  cart_id: string
}

export interface UnitGroupProps {
  unitId: string
  unitName: string
  unitParticipants?: { name: string; id: string }[]
  pricePerParticipant: string
  totalPricePerUnitGroup: string
  participantsCount: number
}
export type bookableAddonType = {
  name: string
  id: string
  requirement?: string
  units: Omit<UnitGroupProps, "unitParticipants">[]
}

export interface GetLiveCapacityByProductIdPayload {
  slot_id: string
  quantities: {
    quantity: number
    unit_id: string
  }[]
}
export interface GetLiveCapacityType {
  is_minimum_fullfilled: boolean
  minimum_capacity_required_for_unit: number
  minimum_participants_required_booking_display_message: string | null
  minimum_participants_required_for_slot: number
  quantity: number
  total_live_capacity: number
  unit_id: string
  formatted_price: string
  unit_name: string
  unit_amount: number
  unit_price: number
  formatted_unit_price: string
  price: number
  pricing_rule: string | null
}
export type GetLiveCapacityByProductIdResponseType = GetLiveCapacityType[]

export interface SavedCardType {
  id: string
  customer_id: string
  payment_token: string
  integration_id: string
  brand: string
  last4: string
  exp_month: string
  exp_year: string
  masked_card_number: string
  name_on_card: string | null
  is_expired: boolean
  is_default: number
  metadata: {
    customer_id: string
    payment_method_id: string
  }
  created_at: string
  updated_at: string
  deleted_at: string | null
  integration: TransactionType["integration"]
}
export type GetSavedCardsOfCustomerResponseType = SavedCardType[]

export interface AuthorizeCardToHoldAmountRequestType {
  integration_id: string
  payment_token: string
  amount: number
  from_saved_card: boolean
  order_id?: string
  payment_intent_id?: string
  name_on_card?: string
}
export interface AuthorizeCardToHoldAmountResponseType {
  code?: string
  payload?: {
    client_secret?: string
    order_id: string
  }
  message: string
}

export interface IPaymentHoldType {
  id: string
  order_id: string
  order_item_id: string | null
  integration: {
    id: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    tenant_id: string
    domain_id: string
    name: string
    type: string
    status: string
    parent_integration_id: string | null
  }
  amount: number
  captured_amount: number
  currency: string
  formatted_amount: string
  formatted_captured_amount: string
  hold_reason: string | null
  cancel_reason: string | null
  release_reason: string
  capture_reason: string
  held_at: string
  expires_at: string | null
  released_at: string | null
  captured_at: string | null
  cancelled_at: string | null
  card_brand: string | null
  card_last4: string | null
  card_exp_month: string | null
  card_exp_year: string | null
  status: PaymentHoldType
  created_at: string
  updated_at: string
}

export interface AddExpenseDiscountToOrderItemRequestType {
  description: string
  amount: number
  levies?: string[]
  booking_id?: string
}

export interface UpdatePriceOfOrderItemRequestType {
  quantities: {
    amount: number
    unit_id: string
    booking_id?: string
  }[]
}

export interface AddAddonToOrderItemRequestType {
  product_id: string
  booking_participant_id?: string
  quantities: {
    quantity: number
    unit_id: string
    booking_id?: string
  }[]
}

export interface AddMultipleAddonToOrderItemRequestType {
  items: AddAddonToOrderItemRequestType[]
}

export type UpdateAddonOfOrderItemRequestType = Omit<AddAddonToOrderItemRequestType, "product_id">

export type UpdateQuantityOfOrderItemRequestType = UpdateAddonOfOrderItemRequestType

export interface GetPerParticipantFieldsOfOrderResponseType {
  data: PerBookingFieldTypeInBookingFields[]
}

export interface UpdatePerParticipantFieldsOfOrderRequestType {
  id?: string
  field_id?: string
  order_item_id: string
  value: string | number | string[] | boolean
  booking_participant_id?: string
}

export interface UpdateOrderCustomerRequestType {
  customer_id: string
}

export interface UpdateOrderAffiliateRequestType {
  affiliate_id: string
}

export interface GetParticipantsOfOrderItemResponseType {
  participants: {
    id: string
    checked_in: CheckInTypes
    check_in_date: string | null
    check_out_date: string | null
    status: OrderStatusTypes
    fields: Record<string, string>
    documents_count: number
    boating_license_documents_count: number
    cart_participant: {
      cart_id: string
      unit_id: string
      id: string
      cart_item_id: string
      unit: {
        id: string
        name: string
      }
    }
    bookingCheckInStatus: {
      status: CheckInTypes
      total: number
      completed: number
    }
    waiver_count: {
      total: number
      signed: number
      unsigned: number
    }
    unit: {
      id: string
      name: string
      description: string
      unit_type: string | null
      internal_name: string | null
      product_id: string
      id_required: boolean
      visibility: string
      capacity_used: number
      pricing_structure_type: string
      min_quantity: number
      max_quantity: number | null
      minimum_duration: number
      maximum_duration: number
      is_multi_day: number
      has_flexible_timings: number
      is_calculation_based_on_calendar_days: number
      duration: number
      is_associated: boolean
      price_type: string
      created_at: string
      updated_at: string
    }
  }[]
}

export enum ActionLogTypes {
  ORDER = "Order",
  CUSTOMER = "Customer",
  TRANSACTION = "Transaction",
  BOOKING = "Booking",
  PRODUCT = "Product",
  ASSET = "Asset",
  DATA_VIEW = "DataView",
  PRODUCT_AVAILABILITY = "ProductAvailability",
  OFFER = "Offer",
}
export interface GetActionsLogResponseType {
  data: {
    id: string
    "@context": string
    type: string
    priority: number
    actor: {
      type: string
      id: string
      name: string
    }
    subject: {
      id: string
      name: string
      type: ActionLogTypes
    }
    arguments: Record<string, string>
    published: string
    result: {
      type: string
      description: string
      changes: {
        column: string
        display: string
        new_value: string
        old_value: string
      }[]
    }
  }[]
  links: PaginationLinkType
  meta: MetaDataType
}
export interface GetMembershipDiscountsResponseType {
  data: ProductMembershipDiscountType[]
  links: PaginationLinkType
  meta: MetaDataType
}

export enum ApplyMembershipCodeType {
  MEMBERSHIP_CODE = "membership_code",
  MEMBER_CODE = "member_code",
  PHONE = "phone",
}

export interface ApplyMembershipToCartRequestType {
  code_type: ApplyMembershipCodeType
  code: string
}

export interface ApplyMembershipToCartResponseType {
  cart: GetCartResponseType["data"]
  message: string
  code: number
  applicable_membership: {
    cancellation_date: string | null
    cancellation_reason: string | null
    code: string
    created_at: string
    customer: CustomerTypeOrder
    customer_id: string
    deleted_at: string | null
    domain: IDomain
    domain_id: string
    end_at: string | null
    id: string
    is_active: number
    order_id: string
    order_item_id: string
    product: OrderItemProductType
    product_id: string
    start_at: string
    tenant_id: string
    updated_at: string
  }
}

export interface CustomerMembershipResponseType {
  customerMembership: {
    id: string
    start_at: string
    end_at: string | null
    product: {
      id: string
      name: string
      code: string
      description: string
      terms_and_conditions: null
      cancellation_policy: null
      widget_description: string
      has_required_info: boolean
      visibility: string
      is_rental: boolean
      is_dynamic_rental: boolean
      is_multi_day_rental: boolean
      is_flexible_timings_rental: boolean
      status: string
      mediaFiles: []
      advertise_price: null
      reminders: []
      prices: []
      type: {
        id: string
        name: string
        is_rental: boolean
        description: string
        internal_type: string
      }
      categories: {
        id: string
        name: string
        type: string
        description: null
        created_at: string
        visibility: string
        updated_at: string
        cover_image: null
      }[]
      availability: null
      merchandise: []
      gift_card: []
      membership: {
        price: { price: number; display_price: string; price_formatted: string }
        validity: { end_at: string; start_at: string; expiry_type: string; expiry_value: number }
        limitation: {
          duration_type: string
          duration_value: number
          max_no_of_booking: string
          has_booking_limitation: boolean
          can_combine_with_offers: boolean
        }
      }
      minimum_giftcard_value: null
      minimum_participants: number
      minimum_participants_booking_display_message: null
      has_addon: number
      widget_quantity_selector_label: null
      created_at: string
    }
    code: string
    cancellation_date: null
    cancellation_reason: null
    is_active: number
    order: {
      order_number: string
      id: string
      customer: {
        id: string
        first_name: string
        last_name: string
        email: string
        phone: string
        phone_code: string
        address1: null
        address2: null
        city: null
        state: null
        country: null
        zip_code: null
        created_at: string
        updated_at: string
        status: string
        language_iso2: string
        profile_image: null
        is_member: number
        member_code: string
        phone_verified_at: null
        email_verified_at: null
        is_temp_email: boolean
      }
      status: string
      total_formatted: string
      total_due_formatted: string
      total_paid_formatted: string
      channel: string
      created_by: string
      creator_type: string
      created_at: string
      items: []
      currency: string
    }
    customer: {
      id: string
      first_name: string
      last_name: string
      email: string
      phone: string
      phone_code: string
      address1: null
      address2: null
      city: null
      state: null
      country: null
      zip_code: null
      created_at: string
      updated_at: string
      status: string
      language_iso2: string
      profile_image: null
      is_member: number
      member_code: string
      phone_verified_at: null
      email_verified_at: null
    }
  }
}

export interface MembershipUsageResponseType {
  usage: {
    booking_limitation: {
      allowed: number
      used: number
      is_limit_reached: boolean
      limitation: {
        duration_type: string
        duration_value: string
        max_no_of_booking: number
        has_booking_limitation: boolean
        can_combine_with_offers: boolean
      }
    }
    product_limitation: {
      product: { id: string; name: string }
      allowed: number
      used: number
      is_limit_reached: boolean
      limitation: {
        duration_type: string
        duration_value: string
        max_no_of_booking: number
        has_booking_limitation: boolean
        can_combine_with_offers: boolean
      }
      usage_details: {
        order_id: string
        order_number: string
        order_date: string
      }[]
    }[]
  }
  customerMembership: {
    id: string
    start_at: string
    end_at: string
    product: {
      id: string
      name: string
      code: string
      description: string
      terms_and_conditions: null
      cancellation_policy: null
      widget_description: string
      has_required_info: boolean
      visibility: string
      is_rental: boolean
      is_dynamic_rental: boolean
      is_multi_day_rental: boolean
      is_flexible_timings_rental: boolean
      status: string
      mediaFiles: []
      advertise_price: null
      reminders: []
      prices: []
      type: {
        id: string
        name: string
        is_rental: boolean
        description: string
        internal_type: string
      }
      categories: {
        id: string
        name: string
        type: string
        description: null
        created_at: string
        visibility: string
        updated_at: string
        cover_image: null
      }[]
      availability: null
      merchandise: []
      gift_card: []
      membership: {
        price: { price: number; display_price: string; price_formatted: string }
        validity: {
          end_at: string
          start_at: string
          expiry_type: string
          expiry_value: string
        }
        limitation: {
          duration_type: string
          duration_value: string
          max_no_of_booking: number
          has_booking_limitation: boolean
          can_combine_with_offers: boolean
        }
      }
      minimum_giftcard_value: null
      minimum_participants: number
      minimum_participants_booking_display_message: null
      has_addon: number
      widget_quantity_selector_label: null
      created_at: string
    }
    code: string
    cancellation_date: null
    cancellation_reason: null
    is_active: number
    order: {
      order_number: string
      id: string
      customer: {
        id: string
        first_name: string
        last_name: string
        email: string
        phone: string
        phone_code: string
        address1: null
        address2: null
        city: null
        state: null
        country: null
        zip_code: null
        created_at: string
        updated_at: string
        status: string
        language_iso2: string
        profile_image: null
        is_member: number
        member_code: string
        phone_verified_at: null
        email_verified_at: null
      }
      status: string
      total_formatted: string
      total_due_formatted: string
      total_paid_formatted: string
      channel: string
      created_by: string
      creator_type: string
      created_at: string
      items: []
      currency: string
    }
    customer: {
      id: string
      first_name: string
      last_name: string
      email: string
      phone: string
      phone_code: string
      address1: null
      address2: null
      city: null
      state: null
      country: null
      zip_code: null
      created_at: string
      updated_at: string
      status: string
      language_iso2: string
      profile_image: null
      is_member: number
      member_code: string
      phone_verified_at: null
      email_verified_at: null
    }
  }
}

export enum QRCodeTypes {
  customerMembership = "CustomerMembership",
  booking = "Booking",
}

export enum MembershipRestrictions {
  NONE = "none",
}

export interface IssueGiftcardForRefundRequestType {
  amount: string
  start_at: string // 2025-02-08 09:00:00
  email: string
  message?: string
  communication_template_id?: string
}

export interface getUnitsWithLiveCapacityRequestType {
  slot_id: string
}

export interface GetUnitsWithBookedCountResponseType {
  data: {
    unit_id: string
    unit_name: string
    booked_count: number
  }
}
